import { ViewType } from '@/shared/types/sourcing-marketplace';
import React, { useEffect, useState } from 'react';
import Router from 'next/router';
import Spinner from '@/components/Common/Spinner';
import SourcingFilters from '@/components/SourcingMarketplace/BrowseSourcing/SourcingFilters';
import SourcingHeader from '@/components/SourcingMarketplace/BrowseSourcing/SourcingHeader';
import { CategoryConfig, VerticalName } from '@/shared/types/category';
import { AllCategoryMap, CatalogCategory, CatalogCompany } from '@/shared/types/catalog';
import { BROWSE_MARKETPLACE_TABS, BROWSE_SOURCING_SCROLL_CONTAINER } from '@/shared/constants/sourcing-marketplace';
import { PublicSourcingMarketplaceCategoryPage } from '@/shared/types/business-setting';
import cN from 'classnames';
import { Tabs } from 'antd';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import './index.less';
import SourcingBreadcrumbs from '@/components/SourcingMarketplace/Common/SourcingBreadcrumbs';
import SourcingCategoryFilter from '@/components/SourcingMarketplace/BrowseSourcing/CategoryFilter';

interface BrowseSourcingLayoutProps {
  isMobile: boolean;
  category: CategoryConfig;
  vertical: VerticalName;
  viewType: ViewType;
  categoryList: CatalogCategory[];
  searchCounts: { [key: string]: number };
  categoryConfig: { [key: string]: CategoryConfig[] };
  defaultPopularSuppliers?: CatalogCompany[];
  categoryPage?: PublicSourcingMarketplaceCategoryPage;
  children: React.ReactNode;
  allL1ToL2ToL3Map?: AllCategoryMap;
  ignoreL3CategoryIds?: number[];
  L3IdToCountMap?: { [key: string]: number };
}

const BrowseSourcingLayout = ({
  isMobile,
  category,
  vertical,
  viewType,
  categoryList,
  searchCounts,
  categoryConfig,
  defaultPopularSuppliers,
  categoryPage,
  children,
  allL1ToL2ToL3Map,
  ignoreL3CategoryIds,
  L3IdToCountMap,
}: BrowseSourcingLayoutProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [lastScroll, setLastScroll] = useState<number>(0);

  const params: any = useParams();

  const showMobileHeader = isMobile && lastScroll > 100;
  const isServices = vertical === VerticalName.SERVICES;

  useEffect(() => {
    const onStart = () => {
      setLoading(true);
    };
    const onEnd = () => {
      setLoading(false);
    };

    // Update loading indicator to show once route changes, and hide loading upon
    // load of new page.
    Router.events.on('routeChangeStart', onStart);
    Router.events.on('routeChangeComplete', onEnd);
    Router.events.on('routeChangeError', onEnd);

    document.addEventListener('scroll', handleScroll, false);
    return () => {
      document.removeEventListener('scroll', handleScroll, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = () => {
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    setLastScroll(currentScroll);
  };

  const renderLoader = (
    <div className="loading-container">
      <Spinner />
    </div>
  );

  const renderMobileHeader = (
    <div className={cN('mobile-header', { show: showMobileHeader })}>
      <Tabs
        className="tabs"
        defaultActiveKey={viewType}
        activeKey={viewType}
        items={BROWSE_MARKETPLACE_TABS(isServices, params?.category).map(
          (item: { key: ViewType; label: string; url: string }) => {
            return {
              key: item.key,
              label: <Link href={item.url}>{item.label}</Link>,
            };
          },
        )}
      />
    </div>
  );

  return (
    <div className="browse-sourcing-layout">
      {renderMobileHeader}
      <SourcingBreadcrumbs usedLocation="FDL_and_PDL" isMobileFromServer={isMobile} />
      <SourcingHeader
        isMobile={isMobile}
        category={category}
        vertical={vertical}
        viewType={viewType}
        categoryList={categoryList}
        searchCounts={searchCounts}
        categoryConfig={categoryConfig}
        defaultPopularSuppliers={defaultPopularSuppliers}
        categoryPage={categoryPage}
        allL1ToL2ToL3Map={allL1ToL2ToL3Map}
        ignoreL3CategoryIds={ignoreL3CategoryIds}
        L3IdToCountMap={L3IdToCountMap}
      />
      {viewType !== ViewType.RFP ? (
        <>
          <div className="browse-container">
            {!isMobile && (
              <div className="sourcing-filter-box">
                <SourcingCategoryFilter
                  L2Vertical={vertical}
                  L2CategoryConfig={category}
                  allL1ToL2ToL3Map={allL1ToL2ToL3Map}
                  ignoreL3CategoryIds={ignoreL3CategoryIds}
                  L3IdToCountMap={L3IdToCountMap}
                  viewType={viewType}
                  allEntryPointCategoryConfig={categoryConfig}
                />
                <SourcingFilters viewType={viewType} />
              </div>
            )}
            <>
              {loading ? (
                renderLoader
              ) : (
                <div className="results" id={BROWSE_SOURCING_SCROLL_CONTAINER}>
                  {children}
                </div>
              )}
            </>
          </div>
        </>
      ) : (
        <>{loading ? renderLoader : children}</>
      )}
    </div>
  );
};

export default BrowseSourcingLayout;
